import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router'
import { observer } from 'mobx-react'
import { Container, Row } from 'react-bootstrap'
import { observable } from 'mobx'
import loadable from '@loadable/component'
import { mysiteContainer } from 'src/models/Mysite'
import { personalOfferContainer } from 'src/models/PersonalOffer'
import {
  isEquinoxMysite,
  getImageUrl,
  getSocialMediaType,
  isEquinoxPOSite,
  getMysiteSponsor,
  isMysiteOrPOHomePage,
} from 'src/utils/mySiteUtils'
import { getLocaleCodeFromUrl, application, i18nTranslate } from 'src/utils'
import { IS_BROWSER } from 'src/utils/application'
import { enablePORedesignFeature } from 'src/utils/commonUtils'
import { publish, subscribe, unSubscribeEvent } from 'src/pubsub/subscribe'
import { PUBSUB } from 'src/pubsub/pubEventNames'
import './styles.scss'
const MySiteShopHeaderComponent = loadable(
  () =>
    import('@nuskin/react-mysite-elements').then(module => {
      if (module?.MysiteHero) {
        return module.MysiteHero
      }
      console.error(
        'MysiteHero component not found in @nuskin/react-mysite-elements'
      )
      return () => null
    }),
  {
    fallback: <div />,
    ssr: false,
  }
)

// Kick off the chunk download as soon as this module is parsed in the
// browser so it's already in-flight by the time the PO landing page
// renders the header. Scoped to PO URLs only — Mysite continues to use
// the original lazy/componentDidMount preload path.
if (IS_BROWSER && window.location?.pathname?.includes('/personal-offer/')) {
  try {
    MySiteShopHeaderComponent.preload()
  } catch (err) {
    console.error('MySiteShopHeaderComponent preload failed:', err)
  }
}

const CSS_CLASSES = {
  NUSKIN_HEADER_CONTAINER: '.nu-header-container',
  COMPACT_VIEW: 'compact-view',
  FULL_VIEW: 'full-view',
  MYSITE_COMPACT: 'mysite-compact',
  MYSITE_FULL: 'mysite-full',
  MYSITE_SHOP_HEADER: 'mysite-shop-header',
  JUSTIFY_CONTENT_CENTER: 'justify-content-center',
}

const URL_PREFIXES = {
  HTTPS: 'https://',
  HTTP: 'http://',
  PROTOCOL_RELATIVE: '//',
}

const ERROR_MESSAGES = {
  HEADER_HEIGHT_CALC_ERROR: 'Could not calculate header height, using fallback',
}

const HEADER_POSITIONS = {
  COMPACT: {
    desktop: 85,
    tablet: 70,
    mobile: 58,
  },
  FULL: {
    desktop: 110,
    tablet: 100,
    mobile: 58,
  },
}

@observer
class MysiteShopHeader extends Component {
  @observable isComponentLoaded = false
  @observable headerType = CSS_CLASSES.FULL_VIEW
  @observable totalSavingsFromPubsub = null
  discountSubscriptionToken = null

  constructor(props) {
    super(props)
    this.currentLocale = getLocaleCodeFromUrl({
      locationProps: props,
      at: 'pathParam',
      defaultLocale: 'en_US',
      isReverseType: true,
    })
    this.headerRef = React.createRef()
    this.rafId = null
    this.scrollCheckRaf = null
    this.nuskinHeaderElement = null
    // Pre-flip the loaded flag for PO URLs so the outer Container
    // renders on the very first pass (instead of waiting for
    // componentDidMount to trigger a second render). This removes the
    if (IS_BROWSER && isEquinoxPOSite()) {
      this.isComponentLoaded = true
    }
  }

  componentDidMount() {
    const isPOSite = isEquinoxPOSite()
    const isMysite = isEquinoxMysite()

    if (isMysite || isPOSite) {
      MySiteShopHeaderComponent.preload()
      this.isComponentLoaded = true
      this.initializeInitialState()
    }

    if (isPOSite && enablePORedesignFeature()) {
      this.discountSubscriptionToken = subscribe(
        PUBSUB.PERSONAL_OFFER_DISCOUNT,
        (msg, data) => {
          const discount = data?.discount
          const isValidDiscount =
            typeof discount === 'string' ||
            (typeof discount === 'number' && isFinite(discount))
          this.totalSavingsFromPubsub = isValidDiscount ? discount : null
        }
      )

      // Ask personal-offer-mfe to replay latest discount in case publish happened before this subscription.
      publish(PUBSUB.PERSONAL_OFFER_DISCOUNT_REQUEST)
    }
  }

  componentWillUnmount() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
    }
    if (this.scrollCheckRaf) {
      cancelAnimationFrame(this.scrollCheckRaf)
    }
    // Unsubscribe from discount event
    if (this.discountSubscriptionToken) {
      unSubscribeEvent(this.discountSubscriptionToken)
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location?.pathname !== this.props.location?.pathname) {
      if (!this.isComponentLoaded && (isEquinoxMysite() || isEquinoxPOSite())) {
        MySiteShopHeaderComponent.preload()
        this.isComponentLoaded = true
      }
      this.initializeInitialState()
    }
  }

  initializeInitialState = () => {
    if (typeof window === undefined) return

    this.nuskinHeaderElement = document.querySelector(
      CSS_CLASSES.NUSKIN_HEADER_CONTAINER
    )

    if (this.nuskinHeaderElement) {
      const isCompact = this.nuskinHeaderElement.classList.contains(
        CSS_CLASSES.COMPACT_VIEW
      )
      this.headerType = isCompact
        ? CSS_CLASSES.COMPACT_VIEW
        : CSS_CLASSES.FULL_VIEW
    }
  }

  calculateInitialTopPosition = () => {
    const nuskinHeader =
      this.nuskinHeaderElement ||
      document.querySelector(CSS_CLASSES.NUSKIN_HEADER_CONTAINER)
    if (!nuskinHeader) return 0

    try {
      const actualHeight = nuskinHeader.getBoundingClientRect().height
      return Math.round(actualHeight)
    } catch (error) {
      console.warn(ERROR_MESSAGES.HEADER_HEIGHT_CALC_ERROR, error)
      try {
        const isCompact = nuskinHeader.classList.contains(
          CSS_CLASSES.COMPACT_VIEW
        )
        const headerState = isCompact ? 'COMPACT' : 'FULL'
        const deviceType = application.deviceType
        return HEADER_POSITIONS[headerState][deviceType]
      } catch (fallbackError) {
        console.error(
          'Failed to calculate header position fallback:',
          fallbackError
        )
        return 0
      }
    }
  }

  formatExpirationDate = timestamp => {
    if (!timestamp) return null

    try {
      const date = new Date(timestamp)
      // timeZone: 'UTC' keeps this in sync with the UTC day the offer's expiresAt was anchored
      // to; without it, browsers ahead of UTC show the day after the one actually selected.
      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
      }
      return date.toLocaleDateString('en-US', options)
    } catch (error) {
      console.error('Error formatting expiration date:', error)
      return null
    }
  }

  formatSocialMediaLinks = (socialAccounts = []) => {
    if (!socialAccounts || !Array.isArray(socialAccounts)) {
      return []
    }

    return socialAccounts.map(item => {
      const url = item?.url ?? ''
      const formattedUrl =
        url &&
        !url.startsWith(URL_PREFIXES.HTTPS) &&
        !url.startsWith(URL_PREFIXES.HTTP)
          ? `${URL_PREFIXES.PROTOCOL_RELATIVE}${url}`
          : url

      return {
        type: getSocialMediaType(formattedUrl),
        url: formattedUrl,
      }
    })
  }

  getFallbackData = () => {
    const poBannerFallbacks = personalOfferContainer.getDefaultBannerForPO
    return {
      poBannerFallbacks,
      fallbackSocialMediaLink: this.formatSocialMediaLinks(
        poBannerFallbacks?.socialIcons
      ),
    }
  }

  createImageDetails = (profilePhoto, altText = 'mysite shop image') =>
    profilePhoto
      ? {
          imageSrc: getImageUrl(profilePhoto),
          imageAlt: altText,
          bgColor: '#5F5F5F',
        }
      : null

  getBannerProps = banner => ({
    backgroundColor: banner?.backgroundColor || null,
    headlineFont: banner?.headlineFont || null,
    paragraphFont: banner?.paragraphFont || null,
  })

  formatExpirationDateWithLabel = expirationDate =>
    expirationDate
      ? `${i18nTranslate('mySite.expiration', 'Expires')} ${expirationDate}`
      : null

  formatTotalSavingsLabel = savingsAmount =>
    savingsAmount !== null &&
    savingsAmount !== undefined &&
    savingsAmount !== ''
      ? `${i18nTranslate('mySite.totalSavings', 'Total Savings')}: ${savingsAmount}`
      : null

  getShopHeaderResponse = isPOSite =>
    isPOSite
      ? personalOfferContainer?.poSiteShopHeaderResponse
      : mysiteContainer?.mysiteShopHeaderResponse

  loadPOBannerFallbackData = () => {
    if (!personalOfferContainer.productOffersCSValues) {
      personalOfferContainer.getProductOffersCSValues()
    }
  }

  getMySiteShopHeaderProps = () => {
    try {
      const isPOSite = isEquinoxPOSite()
      const shopHeaderResponse = this.getShopHeaderResponse(isPOSite) || {}
      const { user = {}, market = {} } = shopHeaderResponse
      const region = this.currentLocale?.split('_')?.[0] || 'en'
      const shopDetailsByLanguage = market?.lang?.[region]
      const shopName = user?.shopName
      const isHomePage = isMysiteOrPOHomePage()

      const socialMediaLink = this.formatSocialMediaLinks(user?.socialAccounts)

      if (isPOSite) {
        const shouldUseFallbackForPO =
          !user?.userId || personalOfferContainer.hasPOSiteHeaderApiFailed()

        if (shouldUseFallbackForPO) {
          this.loadPOBannerFallbackData()
          const { poBannerFallbacks = {} } =
            this.getFallbackData()

          const poData = personalOfferContainer?.personalOfferData
          const isProductOfferV2Enabled = enablePORedesignFeature()
          const localeParts = this.currentLocale?.split('_') || []
          const poLang = localeParts[0] || ''
          const poMarket = localeParts[1] || ''

          const fallbackShopTitle =
            isProductOfferV2Enabled && poData?.name
              ? poData.name
              : poBannerFallbacks?.shopTitle || null

          const fallbackDescription = isProductOfferV2Enabled
            ? poData?.messages?.[poMarket]?.[poLang]?.description ||
              poBannerFallbacks?.shopDescription ||
              null
            : poBannerFallbacks?.shopDescription || null

          const fallbackExpirationDate = this.formatExpirationDate(
            isProductOfferV2Enabled
              ? poData?.expiresAt || poBannerFallbacks?.expirationDate || null
              : poBannerFallbacks?.expirationDate || null
          )

          return {
            imageDetails: this.createImageDetails(
              poBannerFallbacks?.imageUrl || null,
              poBannerFallbacks?.imageAltText || 'mysite shop image'
            ),
            shopTitle: fallbackShopTitle,
            shopDescription: fallbackDescription,
            socialTitleText: poBannerFallbacks?.socialTitleText || null,
            email: null,
            phone: poBannerFallbacks?.phone || null,
            socialIcons: [],
            bannerProps: this.getBannerProps(
              poBannerFallbacks?.bannerProps || null
            ),
            showBadges: true,
            expirationDate:
              this.formatExpirationDateWithLabel(fallbackExpirationDate) ||
              null,
            shouldStartCollapsed: !isHomePage,
            totalSavings: isProductOfferV2Enabled
              ? this.formatTotalSavingsLabel(this.totalSavingsFromPubsub)
              : null,
          }
        }
      }

      const storeDescription = shopDetailsByLanguage?.storeDescription?.trim()

      const poData = personalOfferContainer?.personalOfferData
      const isProductOfferV2Enabled = isPOSite && enablePORedesignFeature()
      const localeParts = this.currentLocale?.split('_') || []
      const poLang = localeParts[0] || 'en'
      const poMarket = localeParts[1] || 'US'
      const poDescription = isProductOfferV2Enabled
        ? poData?.messages?.[poMarket]?.[poLang]?.description || null
        : null
      const poExpirationDate = isProductOfferV2Enabled
        ? poData?.expiresAt || null
        : poData?.shareLink?.expirationDate || null

      return {
        imageDetails: this.createImageDetails(
          user?.profilePhoto || null,
          'mysite shop image'
        ),
        shopTitle:
          isPOSite && isProductOfferV2Enabled
            ? poData?.name ||
              shopName ||
              shopDetailsByLanguage?.storeName ||
              null
            : shopName || shopDetailsByLanguage?.storeName || null,
        shopDescription: isPOSite
          ? poDescription || storeDescription || null
          : storeDescription ||
            mysiteContainer?.mysiteDefaultCategoryInfo?.siteDescription ||
            null,
        socialTitleText: user?.displayName || null,
        email: user?.email || null,
        phone: user?.phone || null,
        socialIcons: socialMediaLink || [],
        bannerProps: this.getBannerProps(market?.banner || null),
        shouldStartCollapsed: !isHomePage,
        ...(isPOSite && {
          showBadges: true,
          expirationDate:
            this.formatExpirationDateWithLabel(
              this.formatExpirationDate(poExpirationDate)
            ) || null,
          totalSavings: isProductOfferV2Enabled
            ? this.formatTotalSavingsLabel(this.totalSavingsFromPubsub)
            : null,
        }),
      }
    } catch (error) {
      console.error('Error in getMySiteShopHeaderProps:', error)
    }
  }

  render() {
    const { location } = this.props
    const livePathname = (IS_BROWSER && window.location?.pathname) || ''
    const isMysiteOrPOPath =
      livePathname.includes('/personal-offer/') ||
      livePathname.includes('/mysite/')
    const pathname =
      (isMysiteOrPOPath && livePathname) || location?.pathname || ''
    if (
      !pathname.includes('/mysite/') &&
      !pathname.includes('/personal-offer/')
    ) {
      return null
    }
    const mysiteSponsor = getMysiteSponsor()
    const mysiteContainInPath = pathname?.includes?.('/mysite/') || null

    if (
      mysiteContainInPath &&
      isEquinoxMysite() &&
      !pathname?.includes(mysiteSponsor)
    ) {
      return null
    }

    if (isEquinoxMysite() && !mysiteContainer?.mysiteShopHeaderResponse) {
      return null
    }
    const { market } = mysiteContainer?.mysiteShopHeaderResponse || {}
    const isToShowMysiteBanner = market?.banner?.showBanner

    if (isEquinoxMysite() && !isToShowMysiteBanner) {
      return null
    }

    if (!this.isComponentLoaded) {
      return null
    }
    const mySiteShopHeaderProps = this.getMySiteShopHeaderProps()
    const headerStateClass =
      this.headerType === CSS_CLASSES.COMPACT_VIEW
        ? CSS_CLASSES.MYSITE_COMPACT
        : CSS_CLASSES.MYSITE_FULL

    const initialTop = this.calculateInitialTopPosition()

    return (
      <Container
        fluid
        className={`${CSS_CLASSES.MYSITE_SHOP_HEADER} ${headerStateClass}`}
        ref={this.headerRef}
        data-current-path={location?.pathname ?? ''}
        style={{
          top: `${initialTop}px`,
        }}>
        <Row className={CSS_CLASSES.JUSTIFY_CONTENT_CENTER}>
          <MySiteShopHeaderComponent {...mySiteShopHeaderProps} />
        </Row>
      </Container>
    )
  }
}

MysiteShopHeader.propTypes = {
  isMySiteorPOSite: PropTypes.bool,
  location: PropTypes.object,
}

MysiteShopHeader.defaultProps = {
  isMySiteorPOSite: false,
  location: {},
}

export default withRouter(MysiteShopHeader)
